import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { ArrowRight, Mail, Lock, ShieldCheck, Check, Clock } from "lucide-react";
import { api, saveSession, getUser } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import { TESTIDS } from "@/constants/testIds";
import { extractErrorMessage } from "@/lib/format";

const STEPS = { EMAIL: "email", LOGIN: "login", CREATE: "create" };
const CONSULTING_PAY_URL = "https://buy.stripe.com/dRm6oHdUJb7r0xy2QWgnK06";
const DIAGNOSTIC_PAY_URL = "https://buy.stripe.com/fZu7sL8Ap5N7dkk77cgnK05";

export default function Landing() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConsultingModal, setShowConsultingModal] = useState(false);
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);

  async function handleConsultingBuy() {
    const currentUser = getUser();
    if (currentUser?.email) {
      // Logueado → Checkout Session server-side con email BLOQUEADO
      try {
        const r = await api.post("/consulting/checkout-session");
        window.location.href = r.data.url;
      } catch (err) {
        toast.error(extractErrorMessage(err, "No pudimos iniciar el checkout"));
      }
    } else {
      // Sin login → modal con opciones
      setShowConsultingModal(true);
    }
  }

  async function checkEmail(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/check-email", {
        email: email.trim().toLowerCase(),
      });
      if (data.status === "login") setStep(STEPS.LOGIN);
      else if (data.status === "create_password") setStep(STEPS.CREATE);
      else if (data.status === "activate_pending") {
        toast.info(
          "Tu cuenta está pendiente de activación. Revisa tu bandeja de entrada (y spam) — te enviamos un enlace para establecer contraseña. Si el enlace ha caducado, te reenviaremos uno nuevo automáticamente.",
          { duration: 10000 }
        );
      }
      else if (data.status === "pay") {
        toast.info("No encontramos un pago con este email. Te llevamos al checkout.");
        setTimeout(() => {
          window.location.href = data.payment_url;
        }, 1200);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "No pudimos verificar el email"));
    } finally {
      setLoading(false);
    }
  }

  async function submitLogin(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      saveSession(data.access_token, { email: data.email, is_admin: data.is_admin });
      toast.success("Sesión iniciada");
      navigate(data.is_admin ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Error al iniciar sesión"));
    } finally {
      setLoading(false);
    }
  }

  async function submitCreate(e) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/create-password", {
        email: email.trim().toLowerCase(),
        password,
      });
      saveSession(data.access_token, { email: data.email, is_admin: data.is_admin });
      toast.success("Cuenta creada. ¡Bienvenido/a!");
      navigate(data.is_admin ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(extractErrorMessage(err, "No pudimos crear la cuenta"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#fafafa] overflow-x-hidden" data-testid={TESTIDS.landing.root}>
      <Seo
        title="Diagnóstico de Gestión del Talento · OptimizaRRHH · Talent Health Check"
        description="Mide la salud del talento de tu empresa en 7 minutos con el Diagnóstico Rápido (47 €) o profundiza con la Consultoría Premium (397 €). Informe personalizado por IA, benchmark sectorial y plan de acción accionable. +2.400 pymes evaluadas en LATAM y España."
        canonical="https://talenthealthcheck.com/"
        image="https://talenthealthcheck.com/mockups/img/mag-pc-1-hd.png"
      />
      <BrandHeader />
      <main className="relative">
        {/* Textura sutil de puntos + halo verde en la esquina superior derecha
            para dar profundidad al hero sin recargar la pantalla. */}
        <div className="absolute inset-x-0 top-0 h-[720px] grid-dots opacity-70 pointer-events-none" aria-hidden="true"></div>
        <div className="absolute -top-24 -right-32 w-[420px] h-[420px] rounded-full bg-emerald-300/15 blur-3xl pointer-events-none" aria-hidden="true"></div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-14 pb-24">
          {/* Cinta premium: enlace directo a la sección de consultoría */}
          <a
            href="#consultoria"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("consultoria");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="group relative flex items-center justify-between gap-4 rounded-full px-5 py-3 mb-10 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 focus:outline-none"
            style={{
              background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 55%, #0b3d2e 100%)",
              border: "1px solid rgba(201,168,76,0.45)",
              boxShadow: "0 14px 32px -14px rgba(11,61,46,0.55), inset 0 1px 0 rgba(229,200,116,0.22)",
            }}
            data-testid="landing-consulting-ribbon"
          >
            <span aria-hidden className="absolute inset-x-8 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(229,200,116,0.7) 50%, transparent 100%)" }} />
            <span className="relative flex items-center gap-3 min-w-0">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] sm:tracking-[0.24em] px-2 sm:px-2.5 py-1 rounded-full flex-shrink-0" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", color: "#0b3d2e" }}>
                <span className="sm:hidden">Premium</span>
                <span className="hidden sm:inline">Servicio Premium</span>
              </span>
              <span className="text-[13px] sm:text-sm font-semibold truncate" style={{ color: "#E5C874" }}>
                <span className="hidden lg:inline">¿Necesitas un análisis en profundidad con plan de acción y un set de recursos ejecutables? </span>
                <span className="underline decoration-dotted underline-offset-4 decoration-[#C9A84C]/60">
                  <span className="sm:hidden">Consultoría Premium</span>
                  <span className="hidden sm:inline">Descubre la Consultoría Premium</span>
                </span>
              </span>
            </span>
            <span className="relative flex items-center gap-2 text-xs font-semibold flex-shrink-0" style={{ color: "#E5C874" }}>
              <span className="hidden md:inline">por 397€</span>
              <span aria-hidden className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-transform duration-300 group-hover:translate-x-0.5" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", color: "#0b3d2e" }}>
                →
              </span>
            </span>
          </a>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            {/* Left column: pitch */}
            <section className="lg:col-span-7 fade-in-up">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-slate-900 leading-[1.05]">
                La salud del talento<br className="hidden sm:block" /> de tu empresa,{" "}
                <span className="relative inline-block">
                  <span className="brand-gradient-text">medida en 15 </span>
                  <span className="relative inline-block">
                    <span className="brand-gradient-text">minutos.</span>
                    {/* Underline SOLO en móvil bajo la palabra "minutos." */}
                    <svg
                      aria-hidden="true"
                      className="absolute -bottom-2 left-0 w-full h-3 sm:hidden"
                      viewBox="0 0 400 12"
                      fill="none"
                      preserveAspectRatio="none"
                    >
                      <path d="M2 8 Q100 2 200 6 T398 5" stroke="url(#hero-underline-m)" strokeWidth="3" strokeLinecap="round" fill="none" />
                      <defs>
                        <linearGradient id="hero-underline-m" x1="0" x2="1">
                          <stop stopColor="#0F6E56" />
                          <stop offset="1" stopColor="#34D399" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </span>
                  {/* Underline SOLO en desktop bajo toda la frase */}
                  <svg
                    aria-hidden="true"
                    className="absolute -bottom-2 left-0 w-full h-3 hidden sm:block"
                    viewBox="0 0 400 12"
                    fill="none"
                    preserveAspectRatio="none"
                  >
                    <path d="M2 8 Q100 2 200 6 T398 5" stroke="url(#hero-underline)" strokeWidth="3" strokeLinecap="round" fill="none" />
                    <defs>
                      <linearGradient id="hero-underline" x1="0" x2="1">
                        <stop stopColor="#0F6E56" />
                        <stop offset="1" stopColor="#34D399" />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-500 leading-relaxed max-w-xl">
                Un diagnóstico de 30 preguntas sobre 6 áreas críticas: marca empleadora, selección,
                onboarding, evaluación, feedback y desarrollo.
              </p>
              <p className="mt-3 text-base sm:text-lg text-slate-500 leading-relaxed max-w-xl">
                Al terminar recibes un informe PDF con tu puntuación y las áreas a priorizar.
              </p>
              <p className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl">
                ¿Necesitas más? La{" "}
                <a
                  href="#consultoria"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById("consultoria");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="font-semibold underline decoration-dotted underline-offset-4"
                  style={{ color: "#0F6E56", textDecorationColor: "#C9A84C" }}
                  data-testid="hero-consulting-bridge-link"
                >
                  Consultoría (397 €)
                </a>{" "}
                lo incluye todo y suma plan de acción, checklist accionable y 7 recursos operativos listos para desplegar.
              </p>

              {/* Trust bar */}
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-slate-500">
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0f6e56]" strokeWidth={1.75} /> Sin instalar nada</div>
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0f6e56]" strokeWidth={1.75} /> Informe PDF profesional</div>
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0f6e56]" strokeWidth={1.75} /> Confidencial 100%</div>
              </div>
            </section>

            {/* Right column: auth card con halo suave detrás */}
            <section className="lg:col-span-5 relative">
              <div className="absolute -z-10 inset-4 bg-gradient-to-br from-emerald-300/20 to-transparent rounded-3xl blur-2xl" aria-hidden="true"></div>
              <div className="surface-lift p-6 md:p-8 fade-in-up">
              {step === STEPS.EMAIL && (
                <form onSubmit={checkEmail} className="space-y-5">
                  <div>
                    <div className="eyebrow mb-3">Empieza ahora</div>
                    <h2 className="font-display text-2xl font-semibold text-slate-900">Accede a tu Talent Health Check</h2>
                    <p className="text-sm text-slate-500 mt-1.5">
                      Accede escribiendo el email con el que compraste en Stripe.
                    </p>
                  </div>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" strokeWidth={2} /> Email
                    </span>
                    <input
                      type="email"
                      required
                      className="input-field"
                      placeholder="tuemail@empresa.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid={TESTIDS.landing.emailInput}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full"
                    data-testid={TESTIDS.landing.submitBtn}
                  >
                    {loading ? "Comprobando…" : "Continuar"}{" "}
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </button>
                </form>
              )}

              {step === STEPS.LOGIN && (
                <form onSubmit={submitLogin} className="space-y-5" data-testid={TESTIDS.login.root}>
                  <div>
                    <div className="eyebrow mb-3">Bienvenido/a</div>
                    <h2 className="font-display text-2xl font-semibold text-slate-900">Continuar tu sesión</h2>
                    <p className="text-sm text-slate-500 mt-1.5 break-all">{email}</p>
                  </div>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" strokeWidth={2} /> Contraseña
                    </span>
                    <input
                      type="password"
                      required
                      className="input-field"
                      placeholder="Tu contraseña"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid={TESTIDS.login.passwordInput}
                      autoFocus
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full"
                    data-testid={TESTIDS.login.submitBtn}
                  >
                    {loading ? "Entrando…" : "Iniciar sesión"}
                  </button>
                  <div className="flex justify-between items-center text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setStep(STEPS.EMAIL);
                        setPassword("");
                      }}
                      className="text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      ← Cambiar email
                    </button>
                    <a
                      href="/forgot-password"
                      className="text-[#0f6e56] font-medium hover:underline"
                      data-testid={TESTIDS.login.forgotLink}
                    >
                      ¿Olvidaste tu contraseña?
                    </a>
                  </div>
                </form>
              )}

              {step === STEPS.CREATE && (
                <form onSubmit={submitCreate} className="space-y-5" data-testid={TESTIDS.createPassword.root}>
                  <div>
                    <span className="chip chip-solido mb-3">Pago verificado</span>
                    <h2 className="font-display text-2xl font-semibold text-slate-900 mt-3">Crea tu contraseña</h2>
                    <p className="text-sm text-slate-500 mt-1.5 break-all">
                      Cuenta para <strong className="text-slate-700">{email}</strong>
                    </p>
                  </div>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" strokeWidth={2} /> Contraseña (mín. 8 caracteres)
                    </span>
                    <input
                      type="password"
                      required
                      minLength={8}
                      className="input-field"
                      placeholder="Elige una contraseña segura"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid={TESTIDS.createPassword.passwordInput}
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 mb-1.5">Confirmar contraseña</span>
                    <input
                      type="password"
                      required
                      className="input-field"
                      placeholder="Repite tu contraseña"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      data-testid={TESTIDS.createPassword.confirmInput}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full"
                    data-testid={TESTIDS.createPassword.submitBtn}
                  >
                    {loading ? "Creando cuenta…" : "Crear cuenta"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(STEPS.EMAIL)}
                    className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    ← Cambiar email
                  </button>
                </form>
              )}
            </div>
          </section>
        </div>

        {/* Cómo funciona — full-width en desktop debajo del hero */}
        <section className="mt-12 md:mt-16 fade-in-up" data-testid="landing-how">
          <div className="rounded-2xl border border-slate-200/70 bg-white p-6 md:p-10 shadow-[0_2px_12px_-2px_rgba(15,23,42,0.05)]">
            <div className="eyebrow mb-5 md:mb-6">Cómo funciona</div>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                { t: <><strong className="text-slate-900">Elige tu plan.</strong> Diagnóstico rápido (47 €) o Consultoría completa (397 €, incluye el diagnóstico).</> },
                { t: <><strong className="text-slate-900">Paga con Stripe.</strong> Checkout seguro con tarjeta, Apple/Google Pay y otros métodos.</> },
                { t: <><strong className="text-slate-900">Activa tu cuenta.</strong> Recibes un email para establecer tu contraseña. Si ya tenías cuenta, entra con tu email desde arriba.</> },
                { t: <><strong className="text-slate-900">Empieza el análisis.</strong> Diagnóstico en 15 min o cuestionario de consultoría con informe personalizado en 24 h.</> },
              ].map((row, i) => (
                <li key={i} className="flex gap-4">
                  <div className="shrink-0 w-9 h-9 rounded-full brand-gradient text-white font-semibold text-base flex items-center justify-center">
                    {i + 1}
                  </div>
                  <div className="text-[15px] text-slate-700 leading-relaxed pt-1">{row.t}</div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Comparativa visual: Diagnóstico €47 vs Consultoría €397 */}
        <section className="mt-20 fade-in-up" data-testid="landing-comparison">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="inline-block h-px w-10" style={{ background: "#C9A84C" }} />
                <span className="text-[11px] font-semibold tracking-[0.25em] uppercase" style={{ color: "#0F6E56" }}>
                  Compara tus opciones
                </span>
                <span className="inline-block h-px w-10" style={{ background: "#C9A84C" }} />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
                Elige el nivel de análisis que necesita tu empresa
              </h2>
              <p className="mt-3 text-sm md:text-base text-slate-500 mx-auto text-center lg:whitespace-nowrap">
                Empieza rápido con el diagnóstico, o profundiza con la consultoría completa (que incluye el diagnóstico).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Diagnóstico €47 */}
              <div
                id="tier-diagnostico"
                className="relative rounded-3xl p-8 md:p-9 flex flex-col scroll-mt-24"
                style={{
                  background: "linear-gradient(180deg, #EAF7EE 0%, #F3FBF5 100%)",
                  border: "1px solid rgba(15,110,86,0.18)",
                  boxShadow: "0 14px 40px -20px rgba(15,110,86,0.25)",
                }}
                data-testid="tier-diagnostico"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-[0.24em] px-2.5 py-1 rounded-full" style={{ background: "#0F6E56", color: "#E5C874" }}>
                    Diagnóstico rápido
                  </span>
                  <span className="text-xs font-semibold text-slate-400">15 min</span>
                </div>

                {/* Mockup enmarcado · recuadro esmeralda */}
                <div className="relative flex justify-center items-center my-5" data-testid="tier-diagnostico-mockup-wrap">
                  <div
                    className="relative w-[200px] h-[200px] md:w-[220px] md:h-[220px] rounded-[22px] overflow-hidden flex items-center justify-center"
                    style={{
                      background: "radial-gradient(circle at 50% 40%, #12573f 0%, #0b3d2e 70%)",
                      border: "1px solid rgba(52,192,143,0.75)",
                      boxShadow: "0 0 0 4px rgba(52,192,143,0.08), 0 18px 45px -12px rgba(16,185,129,0.55), inset 0 0 30px rgba(52,192,143,0.18)",
                    }}
                    data-testid="tier-diagnostico-mockup-frame"
                  >
                    <img
                      src="/mockups/img/mockup-diagnostico.webp"
                      alt="Vista previa del informe del Diagnóstico Rápido"
                      loading="lazy"
                      className="w-full h-full object-contain p-2"
                      data-testid="tier-diagnostico-mockup"
                    />
                  </div>
                </div>

                <div className="flex items-baseline flex-wrap gap-x-2 gap-y-1 mb-1">
                  <span className="font-display text-4xl font-bold text-slate-900 whitespace-nowrap">47&nbsp;€</span>
                  <span className="text-xs text-slate-400">pago único</span>
                </div>
                <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                  Descubre en 15 minutos las áreas de talento a priorizar en tu empresa.
                </p>
                <ul className="space-y-2.5 text-sm text-slate-700 flex-1 mb-8">
                  {[
                    "Test de 30 preguntas · 6 áreas críticas",
                    "Puntuación cuantitativa por área",
                    "Informe PDF con resumen ejecutivo",
                    "Recomendaciones generales por rango",
                    "Email con las áreas a priorizar",
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#0F6E56" }} strokeWidth={2.5} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setShowDiagnosticModal(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-full py-3 font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    background: "#ffffff",
                    color: "#0F6E56",
                    border: "1.5px solid #0F6E56",
                  }}
                  data-testid="tier-diagnostico-cta"
                >
                  Empezar diagnóstico
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Consultoría €397 · destacada */}
              <div
                className="relative rounded-3xl p-8 md:p-9 flex flex-col"
                style={{
                  background: "linear-gradient(180deg, #0b3d2e 0%, #0F6E56 100%)",
                  border: "1px solid rgba(201,168,76,0.5)",
                  boxShadow: "0 20px 55px -22px rgba(11,61,46,0.6), inset 0 1px 0 rgba(229,200,116,0.22)",
                }}
                data-testid="tier-consultoria"
              >
                <span aria-hidden className="absolute inset-x-10 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(229,200,116,0.7) 50%, transparent 100%)" }} />
                <span
                  aria-hidden
                  className="absolute -top-3 right-6 text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1 rounded-full"
                  style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", color: "#0b3d2e", boxShadow: "0 6px 14px -6px rgba(201,168,76,0.6)" }}
                >
                  Más completo
                </span>
                <div className="flex items-center mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-[0.24em] px-2.5 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.2)", color: "#E5C874", border: "1px solid rgba(201,168,76,0.35)" }}>
                    Consultoría premium
                  </span>
                </div>

                {/* Mockup enmarcado · recuadro dorado */}
                <div className="relative flex justify-center items-center my-5" data-testid="tier-consultoria-mockup-wrap">
                  <div
                    className="relative w-[200px] h-[200px] md:w-[220px] md:h-[220px] rounded-[22px] overflow-hidden flex items-center justify-center"
                    style={{
                      background: "radial-gradient(circle at 50% 40%, #2a3a22 0%, #0b2a20 70%)",
                      border: "1px solid rgba(229,200,116,0.75)",
                      boxShadow: "0 0 0 4px rgba(229,200,116,0.08), 0 18px 45px -12px rgba(201,168,76,0.55), inset 0 0 30px rgba(229,200,116,0.16)",
                    }}
                    data-testid="tier-consultoria-mockup-frame"
                  >
                    <img
                      src="/mockups/img/mockup-consultoria.webp"
                      alt="Vista previa del informe de la Consultoría Premium"
                      loading="lazy"
                      className="w-full h-full object-contain p-2"
                      data-testid="tier-consultoria-mockup"
                    />
                  </div>
                </div>

                <div className="flex items-baseline flex-wrap gap-x-2 gap-y-1 mb-1">
                  <span className="font-display text-4xl font-bold whitespace-nowrap" style={{ color: "#E5C874" }}>397&nbsp;€</span>
                  <span className="text-xs" style={{ color: "rgba(229,200,116,0.7)" }}>pago único · incluye el diagnóstico</span>
                </div>
                <div className="mb-4">
                  <span
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full"
                    style={{
                      background: "rgba(229,200,116,0.12)",
                      color: "#E5C874",
                      border: "1px solid rgba(229,200,116,0.28)",
                    }}
                    data-testid="tier-consultoria-delivery"
                  >
                    <Clock className="w-3 h-3" strokeWidth={2.5} />
                    Informe en 24 h
                  </span>
                </div>
                <p className="text-sm mb-6 leading-relaxed" style={{ color: "#d1fae5" }}>
                  Un análisis profesional a fondo con plan de acción, checklist priorizada y 7 recursos operativos hechos a la medida de tu empresa.
                </p>
                <ul className="space-y-2.5 text-sm flex-1 mb-8" style={{ color: "#d1fae5" }}>
                  {[
                    { t: "Todo lo del Diagnóstico €47", hi: true },
                    { t: "Cuestionario extra: 35 preguntas específicas de tu negocio" },
                    { t: "Informe profesional de consultoría personalizado al 100%" },
                    { t: "Plan de acción a 30, 90 y 180 días" },
                    { t: "Checklist interactiva de acciones priorizadas" },
                    { t: "7 recursos operativos personalizados (ofertas, guías de entrevista, planes de onboarding, evaluación de desempeño y más)" },
                    { t: "Bibliografía profesional y referencias legales" },
                  ].map((row, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: row.hi ? "#E5C874" : "#5eead4" }} strokeWidth={2.5} />
                      <span className={row.hi ? "font-semibold" : ""} style={row.hi ? { color: "#E5C874" } : {}}>
                        {row.t}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handleConsultingBuy}
                  className="group relative inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-sm transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
                  style={{
                    background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                    color: "#0b3d2e",
                    boxShadow: "0 12px 26px -12px rgba(201,168,76,0.6), inset 0 -1px 0 rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.4)",
                    letterSpacing: "0.02em",
                  }}
                  data-testid="tier-consultoria-cta"
                >
                  Empezar consultoría · 397 €
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Card destacada del servicio de Consultoría Premium (€397).
            Diseño editorial premium: dark green + gold eyebrow + serif headline
            con palabra en cursiva + tarjeta blanca central con precio y CTA. */}
        <section id="consultoria" className="mt-20 fade-in-up scroll-mt-24" data-testid="landing-consulting-card">
          <div
            className="relative max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-2xl"
            style={{
              background: "radial-gradient(ellipse at top, #0f7458 0%, #0b5c46 55%, #084a38 100%)",
              border: "1px solid rgba(201,168,76,0.35)",
            }}
          >
            {/* Textura sutil overlay */}
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none opacity-30"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 10%, rgba(201,168,76,0.12) 0px, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.05) 0px, transparent 45%)",
              }}
            />

            <div className="relative px-6 sm:px-12 md:px-16 py-16 md:py-20 text-center">
              {/* Eyebrow */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <span className="inline-block h-px w-10" style={{ background: "#C9A84C" }} />
                <span className="text-[11px] font-semibold tracking-[0.25em] uppercase" style={{ color: "#C9A84C" }}>
                  Servicio Premium
                </span>
                <span className="inline-block h-px w-10" style={{ background: "#C9A84C" }} />
              </div>

              {/* Headline serif con "Talento" en cursiva verde-menta */}
              <h2 className="font-display font-bold leading-[1.05] tracking-tight text-white mb-6" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
                Consultoría de Gestión del <span style={{ color: "#5eead4", fontStyle: "italic", fontWeight: 500 }}>Talento</span>
              </h2>

              {/* Descripción */}
              <div className="max-w-2xl mx-auto text-base md:text-lg leading-relaxed mb-8 space-y-4" style={{ color: "#d1fae5" }}>
                <p>
                  Un paso más allá del diagnóstico. Análisis en profundidad guiado por nuestra
                  metodología de consultoría, sustentada en marcos académicos reconocidos de
                  gestión del talento y psicología organizacional.
                </p>
                <p>
                  Adaptada al marco legal aplicable al país de tu empresa. Incluye informe
                  detallado y plan de acción a 30, 90 y 180 días.
                </p>
                <p>
                  Suma <strong style={{ color: "#5eead4" }}>7 recursos ejecutables</strong> desplegados
                  con velocidad y consistencia por nuestro motor de generación asistida.
                </p>
              </div>

              {/* Lista de 4 bullets */}
              <ul className="max-w-xl mx-auto space-y-2 mb-10 text-sm md:text-[15px] text-left" style={{ color: "#d1fae5" }}>
                <li className="flex items-start gap-3">
                  <span className="mt-1 text-base" style={{ color: "#5eead4" }}>✓</span>
                  <span>Test de 35 preguntas específicas de tu negocio</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 text-base" style={{ color: "#5eead4" }}>✓</span>
                  <span>Informe personalizado listo en 24 horas</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 text-base" style={{ color: "#5eead4" }}>✓</span>
                  <span>Checklist interactiva de acciones priorizadas</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 text-base" style={{ color: "#5eead4" }}>✓</span>
                  <span>Set operativo de 7 piezas listas para aplicar: ofertas, entrevistas estructuradas, planes de onboarding, evaluación y desarrollo</span>
                </li>
              </ul>

              {/* Dos mockups (Diagnóstico + Consultoría) unidos por "+" · CTA debajo */}
              <div className="max-w-2xl mx-auto" data-testid="landing-consulting-bundle">
                <div className="flex items-center justify-center gap-4 md:gap-6">
                  <img
                    src="/mockups/img/mockup-diagnostico.webp"
                    alt="Mockup del Diagnóstico Rápido"
                    loading="lazy"
                    className="w-[42%] md:w-[260px] h-auto object-contain drop-shadow-[0_18px_35px_rgba(4,120,87,0.45)]"
                    data-testid="bundle-mockup-diagnostico"
                  />
                  <div
                    className="font-display font-semibold select-none shrink-0"
                    style={{
                      fontSize: "clamp(2.5rem, 6vw, 4rem)",
                      color: "#C9A84C",
                      textShadow: "0 2px 20px rgba(201,168,76,0.35)",
                      lineHeight: 1,
                    }}
                    aria-hidden
                  >
                    +
                  </div>
                  <img
                    src="/mockups/img/mockup-consultoria.webp"
                    alt="Mockup de la Consultoría Premium"
                    loading="lazy"
                    className="w-[42%] md:w-[260px] h-auto object-contain drop-shadow-[0_18px_35px_rgba(201,168,76,0.5)]"
                    data-testid="bundle-mockup-consultoria"
                  />
                </div>

                <div className="mt-8 flex justify-center">
                  <button
                    onClick={handleConsultingBuy}
                    className="group relative inline-flex items-center justify-center gap-2 rounded-full px-8 md:px-10 py-4 font-bold text-sm md:text-base transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
                    style={{
                      background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                      color: "#0b3d2e",
                      boxShadow: "0 18px 32px -14px rgba(201,168,76,0.65), inset 0 -1px 0 rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.4)",
                      letterSpacing: "0.02em",
                    }}
                    data-testid="landing-consulting-buy-btn"
                  >
                    Comprar consultoría · 397 €
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-10 text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: "rgba(201,168,76,0.75)" }}>
                Pago seguro procesado por Stripe
              </div>
            </div>
          </div>
        </section>

        </div>
      </main>
      <Footer />
      {showDiagnosticModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
          data-testid="diagnostic-cta-modal"
          onClick={() => setShowDiagnosticModal(false)}
        >
          <div
            className="max-w-md w-full rounded-2xl bg-white shadow-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "#0F6E56" }}>
                Diagnóstico · 47 €
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900">
                ¿Qué prefieres hacer?
              </h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Si aún no has pagado, te llevamos al checkout seguro de Stripe. Si ya has pagado, entra con tu email para acceder al diagnóstico.
              </p>
            </div>
            <div className="space-y-2 mt-6">
              <a
                href={DIAGNOSTIC_PAY_URL}
                className="block w-full text-center rounded-full py-3 font-bold text-sm text-white shadow"
                style={{ background: "#0F6E56" }}
                data-testid="diagnostic-buy-btn"
              >
                Comprar ahora · 47 € →
              </a>
              <button
                onClick={() => {
                  setShowDiagnosticModal(false);
                  setStep(STEPS.EMAIL);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  setTimeout(() => {
                    const el = document.querySelector('input[type="email"]');
                    if (el) el.focus();
                  }, 500);
                }}
                className="w-full text-center rounded-full py-3 font-semibold text-sm border-2 transition-colors hover:bg-emerald-50"
                style={{ borderColor: "#0F6E56", color: "#0F6E56", background: "white" }}
                data-testid="diagnostic-already-paid-btn"
              >
                Ya he pagado · Entrar con mi email
              </button>
              <button
                onClick={() => setShowDiagnosticModal(false)}
                className="w-full text-xs text-slate-500 hover:text-slate-900 pt-2"
                data-testid="diagnostic-modal-cancel"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      {showConsultingModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
          data-testid="consulting-login-recommendation-modal"
          onClick={() => setShowConsultingModal(false)}
        >
          <div
            className="max-w-md w-full rounded-2xl bg-white shadow-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "#0F6E56" }}>
                Consultoría · 397 €
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900">
                ¿Qué prefieres hacer?
              </h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Si aún no tienes cuenta, te llevamos al checkout seguro de Stripe y te crearemos la cuenta al recibir el pago. Si ya tienes cuenta, inicia sesión primero para asociar la compra a tu email automáticamente.
              </p>
            </div>
            <div className="space-y-2 mt-6">
              <a
                href={CONSULTING_PAY_URL}
                className="block w-full text-center rounded-full py-3 font-bold text-sm text-white shadow"
                style={{ background: "#0F6E56" }}
                data-testid="consulting-continue-without-login-btn"
              >
                Comprar ahora · 397 € →
              </a>
              <button
                onClick={() => { setShowConsultingModal(false); setStep(STEPS.EMAIL); window.scrollTo({ top: 0, behavior: "smooth" }); setTimeout(() => { const el = document.querySelector('input[type="email"]'); if (el) el.focus(); }, 500); }}
                className="w-full text-center rounded-full py-3 font-semibold text-sm border-2 transition-colors hover:bg-emerald-50"
                style={{ borderColor: "#0F6E56", color: "#0F6E56", background: "white" }}
                data-testid="consulting-login-first-btn"
              >
                Ya tengo cuenta · Iniciar sesión
              </button>
              <button
                onClick={() => setShowConsultingModal(false)}
                className="w-full text-xs text-slate-500 hover:text-slate-900 pt-2"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function PayBadge({ label, bg, fg }) {
  return (
    <span
      className="inline-flex items-center justify-center text-[10px] font-bold rounded"
      style={{ background: bg, color: fg, minWidth: 30, height: 20, padding: "0 6px", letterSpacing: "0.05em" }}
    >
      {label}
    </span>
  );
}
